/**
 * Turning a widget record into the shape a host reads, and the small pure
 * helpers that go with it.
 *
 * Split out of client.ts: none of this touches the client's mutable state, so
 * keeping it inside that closure only made the closure harder to read. Every
 * function here is a pure function of its arguments and is testable on its own.
 */
import type {
  AttachmentLimits,
  ChatCopyOverrides,
  ChatMessageKey,
  ChatState,
  ChatType,
  ContactFormInput,
  WidgetConfig,
} from '../../types';
import { capabilitiesFor } from './capabilities';
import { isWithinBusinessHours, type BusinessHoursSettings } from './business-hours';
import { isRecord } from '../json';
import { countryFromLocale } from '../forms/phone';
import { label } from '../host/i18n';
import { FILE_UPLOAD_CONSTANTS } from '../upload/upload-constants';

export const initialState = (chatType: ChatType): ChatState => ({
  requestState: 'init',
  connection: 'idle',
  config: null,
  activeChatType: chatType,
  awaitingChatTypeSelection: false,
  messages: [],
  hasMoreHistory: false,
  historyLoading: false,
  unreadCount: 0,
  agentTyping: false,
  contactDetailsRequested: false,
  contactFormSubmitted: false,
  showContactForm: false,
  feedbackRequested: false,
  feedbackSubmitted: false,
  detectedCountry: null,
  detectedCountryResolved: false,
  phoneCountry: 'US',
  ended: false,
  endedReason: null,
  canSend: false,
  sendBlockedUntil: null,
  captchaRequired: false,
  captchaStatusMessage: '',
  rateLimited: false,
  rateLimitedUntil: null,
  voice: { status: 'idle', micMuted: false, agentSpeaking: false, elapsed: 0, level: 0 },
  attachments: [],
  error: null,
});

/**
 * The fields a contact form should render.
 *
 * A widget only sends `contact-form-options` when someone has customised it.
 * A real staging widget sends none at all, and the Stencil widget falls back
 * to its own defaults picked by the use-email/use-phone flags. A host given
 * an empty list would render a form with no inputs, so the same fallback
 * belongs here rather than in every host.
 */
export const defaultFields = (): Record<string, Record<string, unknown>> => ({
  name: { id: '1', value: 'name', label: label('name', 'Name'), dataType: 'TEXT', fieldKey: 'contact.name', placeholder: label('name', 'Name'), required: true },
  phone: { id: '2', value: 'phone', label: label('phone', 'Phone'), dataType: 'PHONE', fieldKey: 'contact.phone', placeholder: label('phone', 'Phone'), required: true },
  email: { id: '3', value: 'email', label: label('email', 'Email'), dataType: 'EMAIL', fieldKey: 'contact.email', placeholder: label('email', 'Email'), required: true },
  message: { id: '4', value: 'message', label: label('message', 'Message'), dataType: 'TEXTAREA', fieldKey: 'contact.message', placeholder: label('message', 'Message'), required: true },
});

/**
 * Accept the form values keyed either way a host is likely to key them.
 *
 * Each configured field has two names: `value` ("name", "phone") is the key
 * the forms endpoint reads, and `fieldKey` ("contact.name") is the CRM path it
 * maps to. A host reading the fields to draw inputs sees both, and one that
 * picks `fieldKey` gets a 400 that reads "Missing required fields: name,
 * locationId" — locationId included, because the server trips over the dotted
 * keys before it looks at anything else. The Stencil widget only ever sends
 * `value`; this makes the core do the same regardless of which the host used.
 */
export function normaliseContactFormInput(
  input: ContactFormInput,
  fields: Array<Record<string, unknown>>,
): ContactFormInput {
  const out: Record<string, unknown> = { ...input };
  const move = (from: string, to: string) => {
    if (!(from in out) || from === to) return;
    if (!(to in out) || out[to] === undefined || out[to] === '') out[to] = out[from];
    delete out[from];
  };
  // Configured pairs first, so a custom field with an unusual fieldKey maps to
  // the name the widget was told to post.
  for (const field of fields) {
    const value = typeof field.value === 'string' ? field.value : '';
    const fieldKey = typeof field.fieldKey === 'string' ? field.fieldKey : '';
    if (value && fieldKey) move(fieldKey, value);
  }
  // Then the convention itself: every standard field's fieldKey is "contact."
  // plus its name, and a widget's configured fields do not always carry the
  // fieldKey for the core to look up.
  for (const key of Object.keys(out)) {
    if (key.startsWith('contact.')) move(key, key.slice('contact.'.length));
  }
  return out as ContactFormInput;
}

/**
 * Business hours as the UI needs them. The schedule arrives as a JSON string
 * (`business-office-hours`), parsed by the transport; evaluated here, once, at
 * open — the widget does the same in the pane's first load.
 */
export function businessHoursFor(raw: unknown): WidgetConfig['businessHours'] {
  // Every field of BusinessHoursSettings is optional, so an unrecognised shape
  // narrows to an empty schedule rather than needing a cast.
  const settings: BusinessHoursSettings = isRecord(raw) ? raw : {};
  const enabled = !!settings.enableBusinessHours;
  return {
    enabled,
    withinHours: enabled ? isWithinBusinessHours(settings) : true,
    outsideHoursMessage: settings.outsideOfficeHoursWelcomeMsg || label('offline', 'We are currently offline.'),
  };
}

/** A setting as text, falling back when it is absent or not a string. */
export const settingText = (value: unknown, fallback: string): string =>
  typeof value === 'string' && value.trim() ? value : fallback;

/**
 * Host copy wins over the record; blank strings do not count as copy. Only
 * the lines the core narrates are overridable — compliance text and form
 * fields come from the record alone.
 */
export function applyHostCopy(config: WidgetConfig, copy: ChatCopyOverrides | undefined): WidgetConfig {
  if (!copy) return config;
  const pick = (host: string | undefined, record: string): string =>
    typeof host === 'string' && host.trim() ? host : record;
  const messages: Partial<Record<ChatMessageKey, string>> = config.messages ?? {};
  return {
    ...config,
    contactForm: {
      ...config.contactForm,
      introMessage: pick(copy.contactFormIntroMessage, config.contactForm.introMessage),
      requestMessage: pick(copy.contactFormRequestMessage, config.contactForm.requestMessage),
    },
    liveChat: {
      ...config.liveChat,
      showWelcomeMessage: copy.showWelcomeMessage ?? config.liveChat.showWelcomeMessage,
      welcomeMessage: pick(copy.welcomeMessage, config.liveChat.welcomeMessage),
      feedbackMessage: pick(copy.feedbackMessage, config.liveChat.feedbackMessage),
      feedbackNote: pick(copy.feedbackNote, config.liveChat.feedbackNote),
    },
    businessHours: {
      ...config.businessHours,
      outsideHoursMessage: pick(copy.outsideHoursMessage, config.businessHours.outsideHoursMessage),
    },
    messages: {
      ...messages,
      liveChatVisitorInactiveMsg: pick(copy.visitorInactiveMessage, messages.liveChatVisitorInactiveMsg || ''),
      liveChatUserInactiveMsg: pick(copy.agentUnavailableMessage, messages.liveChatUserInactiveMsg || ''),
      liveChatAckMsg: pick(copy.endedMessage, messages.liveChatAckMsg || ''),
    },
  };
}

/**
 * The fields a contact form should render for the chat type in play.
 *
 * In a live chat the form exists to learn who the visitor is and how to reach
 * them; the message is the chat itself. So the widget (chat-form.tsx,
 * syncInputFormFromConfig) drops the message field there, from the configured
 * list as much as from the defaults, and this does the same. For `both` and
 * all-in-one widgets the type is the visitor's pick, so the fields are
 * derived again when it changes.
 */
export function contactFormFields(settings: Record<string, unknown>, chatType: ChatType): Array<Record<string, unknown>> {
  const liveChat = chatType === 'liveChat';
  const configured = settings.contactFormOptions as Array<Record<string, unknown>> | undefined;
  if (Array.isArray(configured) && configured.length) {
    return liveChat ? configured.filter(field => field.value !== 'message') : configured;
  }

  const defaults = defaultFields();
  const fields = [defaults.name];
  if (settings.usePhoneField !== false) fields.push(defaults.phone);
  if (settings.useEmailField) fields.push(defaults.email);
  if (!liveChat) fields.push(defaults.message);
  return fields;
}

/**
 * The widget's limits, from the same constants its input component reads.
 * Only a thread can carry a file, so the other chat types get `enabled: false`
 * and a host knows to draw no control.
 */
export function attachmentLimitsFor(chatType: ChatType): AttachmentLimits {
  return Object.freeze({
    enabled: capabilitiesFor(chatType).conversation,
    maxFiles: FILE_UPLOAD_CONSTANTS.MAX_FILES,
    maxFileSizeBytes: FILE_UPLOAD_CONSTANTS.MAX_FILE_SIZE,
    acceptedTypes: Object.freeze([...FILE_UPLOAD_CONSTANTS.SUPPORTED_TYPES]),
  });
}

/**
 * The one rule for whether a contact form is on screen, kept in the core so
 * every host gets it right. The mid-chat request stands on its own: it does
 * not depend on the start-of-chat setting, which is the mistake this exists
 * to prevent (a generated site once gated the request card on
 * `contactForm.enabled` and an agent's request rendered nothing).
 */
export function deriveContactForm(s: ChatState): Partial<ChatState> {
  // An SMS/Email chat is only its form; a live chat has one at the start when
  // the record says so. Either way it stays until the server accepts it.
  const formOnly = !capabilitiesFor(s.activeChatType).conversation;
  const atStart = (formOnly || !!s.config?.contactForm.enabled) && !s.contactFormSubmitted;
  const show = !!s.config && !s.awaitingChatTypeSelection && (s.contactDetailsRequested || atStart);
  const phoneCountry = s.detectedCountry ?? countryFromLocale(s.config?.locale) ?? 'US';

  return { showContactForm: show, phoneCountry };
}

export const notImplemented = (what: string) =>
  Promise.reject(new Error(`chat core: ${what} is not implemented yet`));

